import React, { useState } from 'react';
import { IconExternalLink } from '@iwoca/icons';
import { fetchGetStateByStateKey } from '@iwoca/lapi-client/edge';
import { Button, Spinner } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { ICellRendererParams } from 'ag-grid-community';
import { useContinueRedirect } from 'applicationTable/useContinueRedirect.hook';
import clsx from 'clsx';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import {
  TApplication,
  TApplicationFormalOffer,
} from 'lendingApi/applications.types';
import { TGetStateResponseData } from 'lendingApi/lapiOverrides.types';
import { getDESignupImpersonationUrl } from 'lendingApi/lendingApi';
import { Link } from 'react-router-dom';
import { checkApplicationIsComplete } from 'utils/application/application.helpers';
import { APPLICATION_STATUS_VALUE } from 'utils/applications/applications.config';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './ApplicationStatusCell.module.css';
import { ContinueApplicationButton } from './ContinueApplicationButton';

interface ApplicationStatusCellRenderParams extends ICellRendererParams {
  value: TApplication['status'];
  data: TApplication;
}

export const ApplicationStatusCell = ({
  data,
  value,
}: Pick<ApplicationStatusCellRenderParams, 'value' | 'data'>) => {
  const { isDE } = useLocale();
  const { trackEvent } = useAmplitude();
  const { formatCurrency } = useNumberFormat();
  const { getRedirectUrl } = useContinueRedirect();
  const [isLoadingState, setIsLoadingState] = useState(false);
  const { checkIntroducerPermission } = useIntroducerContext();
  const cannotCreateApplication = !checkIntroducerPermission(
    'can_create_applications',
  );
  const cannotInteractWithOffers = !checkIntroducerPermission(
    'can_interact_with_offers',
  );
  const cannotUploadDocuments = !checkIntroducerPermission(
    'can_upload_broker_documents',
  );
  const cannotRefreshOffers = !checkIntroducerPermission('can_refresh_offers');

  const BASE_URL = `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? ':' + window.location.port : ''
  }${
    getEnvironment() !== 'development'
      ? isDE
        ? '/partner/portal'
        : '/introducer/portal'
      : ''
  }`;

  const hasMultipleOffers =
    data?.formal_offer_sets[0]?.formal_offers?.length > 1;

  const applicationPath = PATH_NAMES.application({
    applicationId: data.application_uid,
  });

  const documentsPath = PATH_NAMES.applicationDocuments({
    applicationId: data.application_uid,
  });

  const applicationId = data?.application_uid;
  const stateKey = data?.customer.state_key;
  const paidCommission = data?.paid_commission;

  const renderClass = () => {
    switch (value) {
      case 'funded':
        return styles.greenText;
      case 'declined':
      case 'offer_rejected':
      case 'deferred':
      case 'expired':
        return styles.nonActionableStatus;
      default:
        return styles.actionableStatus;
    }
  };

  const renderFundedCell = ({
    renderCommission,
  }: {
    renderCommission: boolean;
  }) => {
    const { formal_offer_sets } = data;
    const formalOfferWithLoanAgreement =
      formal_offer_sets[0].formal_offers.find(
        (formal_offer: TApplicationFormalOffer) =>
          formal_offer?.loan_agreements &&
          formal_offer?.loan_agreements.length > 0,
      );
    const totalDrawdownAmount =
      formalOfferWithLoanAgreement?.total_drawdown_amount;

    return (
      <div className={renderClass()}>
        <div className={styles.funded}>
          <Trans>Funded</Trans>
        </div>
        <div>
          <Trans>Withdrawn: {formatCurrency(totalDrawdownAmount)}</Trans>
        </div>
        {renderCommission && paidCommission && !isDE && (
          <div>
            <Trans>Commission: {formatCurrency(paidCommission)}</Trans>
          </div>
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (data.is_partner_channel) {
      if (value === 'funded') {
        return renderFundedCell({ renderCommission: false });
      }

      return (
        <span className={styles.actionableStatus}>
          {APPLICATION_STATUS_VALUE(value)}
        </span>
      );
    }

    switch (value) {
      case 'action_required':
        if (cannotUploadDocuments) {
          return (
            <span className={styles.noPermissionStatus}>
              <Trans>Docs required</Trans>
            </span>
          );
        }
        return (
          <Link to={documentsPath}>
            <Button
              variant="action"
              size="small"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackEvent(AMPLITUDE_EVENTS.dashboardCTAClicked, {
                  Status: 'Action required',
                });
              }}
            >
              <Trans>Docs required</Trans>
            </Button>
          </Link>
        );

      case 'expired': {
        const { customer } = data;

        if (
          customer.has_open_application ||
          customer.has_active_formal_offer ||
          cannotRefreshOffers
        ) {
          return (
            <span
              className={
                cannotRefreshOffers ? styles.noPermissionStatus : renderClass()
              }
            >
              {APPLICATION_STATUS_VALUE(value)}
            </span>
          );
        }

        return (
          <Link to={applicationPath}>
            <Button
              variant="denim"
              size="small"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackEvent(AMPLITUDE_EVENTS.dashboardCTAClicked, {
                  Status: 'Expired',
                });
              }}
            >
              <Trans>Refresh app</Trans>
            </Button>
          </Link>
        );
      }

      case 'conditional_offer':
        if (cannotInteractWithOffers) {
          return (
            <span className={styles.noPermissionStatus}>
              <Trans>View offer(s)</Trans>
            </span>
          );
        }
        return (
          <Link to={applicationPath}>
            <Button
              variant="success"
              size="small"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackEvent(AMPLITUDE_EVENTS.dashboardCTAClicked, {
                  Status: 'Conditional offer',
                });
              }}
            >
              Conditional offer{hasMultipleOffers ? 's' : ''}
            </Button>
          </Link>
        );
      case 'offered':
        if (cannotInteractWithOffers) {
          return (
            <span className={styles.noPermissionStatus}>
              <Trans>View offer(s)</Trans>
            </span>
          );
        }
        return (
          <Link to={applicationPath}>
            <Button
              variant="success"
              size="small"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackEvent(AMPLITUDE_EVENTS.dashboardCTAClicked, {
                  Status: 'Offered',
                });
              }}
            >
              {hasMultipleOffers ? t`Review offers` : t`Review offer`}
            </Button>
          </Link>
        );

      case 'funded':
        return renderFundedCell({ renderCommission: true });

      case 'open': {
        const applicationId = data.application_uid;
        const stateKey = data.customer.state_key;
        const formalOffer = data.formal_offer_sets?.[0]?.formal_offers?.[0];
        const applicationIsComplete = checkApplicationIsComplete(data);

        const handleOpenApplicationTracking = () => {
          trackEvent(AMPLITUDE_EVENTS.dashboardCTAClicked, {
            Status: 'Open',
          });

          if (!applicationIsComplete) {
            trackEvent(AMPLITUDE_EVENTS.signupCTAClicked, {
              'CTA location': 'Application table',
              'CTA text': 'Continue app',
              'CTA URL': getRedirectUrl(applicationId),
            });
          }
        };

        if (!formalOffer || (formalOffer && value === 'open')) {
          if (!applicationIsComplete) {
            // TODO: Move this outside of the if block to prevent it only showing up if isSignupFinished is false.
            if (cannotCreateApplication) {
              return (
                <span className={styles.noPermissionStatus}>
                  <Trans>App in progress</Trans>
                </span>
              );
            }
            return (
              <ContinueApplicationButton
                stateKey={stateKey}
                applicationId={applicationId}
              />
            );
          }

          // TODO: remove when moving over to new signup
          if (applicationIsComplete) {
            if (cannotUploadDocuments) {
              return (
                <span className={styles.noPermissionStatus}>
                  <Trans>Docs required</Trans>
                </span>
              );
            }
            return (
              <Link to={documentsPath}>
                <Button
                  variant="action"
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.stopPropagation();
                    handleOpenApplicationTracking();
                  }}
                >
                  <Trans>Docs required</Trans>
                </Button>
              </Link>
            );
          }

          return (
            <Button
              className="linkButton"
              size="small"
              href={getDESignupImpersonationUrl(stateKey)}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                handleOpenApplicationTracking();
              }}
              target="_blank"
              rel="noreferrer"
            >
              <Trans>Continue app</Trans>
            </Button>
          );
        }
        return (
          <span className={renderClass()}>
            {APPLICATION_STATUS_VALUE(value)}
          </span>
        );
      }
      default:
        return (
          <span className={renderClass()}>
            {APPLICATION_STATUS_VALUE(value)}
          </span>
        );
    }
  };

  const openApplicationInLatestStep = async () => {
    try {
      setIsLoadingState(true);
      const response = await fetchGetStateByStateKey({ stateKey });
      const state = response.data as TGetStateResponseData;

      window.open(
        `${BASE_URL}${getRedirectUrl(
          applicationId,
          state.ui?.tip_latest_signup_step,
        )}`,
        '_blank',
      );
    } catch {
      window.open(`${BASE_URL}${getRedirectUrl(applicationId)}`, '_blank');
    } finally {
      setIsLoadingState(false);
    }
  };

  const handleOpenNewTabClick = async (e: MouseEvent) => {
    e.stopPropagation();
    trackEvent(AMPLITUDE_EVENTS.openApplicationInNewTabClicked, {
      'Application status': value,
    });

    if (data.is_partner_channel) {
      window.open(`${BASE_URL}${applicationPath}`, '_blank');
    }

    if (value === 'open') {
      return await openApplicationInLatestStep();
    }

    if (value === 'action_required') {
      window.open(`${BASE_URL}${documentsPath}`, '_blank');
      return;
    }

    window.open(`${BASE_URL}${applicationPath}`, '_blank');
    return;
  };

  return (
    <div className={styles.applicationStatusContainer}>
      {renderStatus()}
      <Button
        className={clsx(styles.newTabButton, {
          [styles.newTabButtonDE]: isDE,
        })}
        data-testid="open-in-new-tab-button"
        variant="tertiary"
        ref={(ref) => {
          // https://stackoverflow.com/a/24421834
          // React uses event delegation with a single event listener on document for events that bubble, which means stopping propagation is not possible;
          // the real event has already propagated by the time you interact with it in React. StopPropagation on React's synthetic event is possible because
          // React handles propagation of synthetic events internally.
          if (!ref) return;

          ref.onclick = async (e) => {
            await handleOpenNewTabClick(e);
          };
        }}
      >
        {isLoadingState ? (
          <Spinner />
        ) : (
          <IconExternalLink className={styles.newTabIcon} />
        )}
      </Button>
    </div>
  );
};
